// Event contracts (EVT-*).
//
// An Event is something happening for the supported person. It is NOT a request for
// help by itself — needs are separate records with their own windows (NED-002), which
// is why nothing here mentions capacity or helpers.
//
// The field list is EVT-002's, in the order plan §9.5 lists it: title, privacy-safe
// share title, category, start/end where known, location label and private detail,
// practical note. It matches the kernel's `EventRecord` and the `events` table one
// for one — a contract that described a different event than the schema stores would
// be a decoupler pointing at nothing.
//
// `startsAt`/`endsAt` are OPTIONAL by design (plan §9.5 "start/end where known"): a
// need can be raised before the hospital confirms a time, and EVT-005 forbids
// treating an event window as a help window regardless — so an event with no time
// yet is a normal record, not a half-built one.

import { z } from "zod";

import type { EventRejection, Result } from "../errors.js";
import { circleIdSchema, eventIdSchema, instantSchema, revisionSchema } from "../primitives.js";

/**
 * The private title. Never appears in logs, analytics, URLs or share links
 * (plan §16.18).
 */
const titleSchema = z.string().min(1, "An event needs a name.").max(200);

/**
 * The title safe to show to somebody holding only a share link (EVT-017).
 *
 * Validated SEPARATELY from `title` rather than derived from it, because the whole
 * point is that a person who may not see "Oncology follow-up" may see "Appointment".
 * Deriving one from the other would mean the private wording decided the public one.
 */
const shareTitleSchema = z.string().min(1, "A share title is needed.").max(120);

/** Free-text category (plan §9.5 lists the shapes; the schema stores a string). */
const categorySchema = z.string().min(1, "Choose what kind of thing this is.").max(60);

const locationLabelSchema = z.string().max(200);
/** Private location detail. Excluded from shares, notifications and history. */
const locationPrivateSchema = z.string().max(500);
const practicalNoteSchema = z.string().max(2000);

/**
 * An optional field on an EDIT.
 *
 * Three states, not two: absent means "leave it alone", a value means "set it", and
 * `null` means "remove it". Without the third, a practical note that turned out to be
 * wrong could only ever be overwritten with a space.
 */
const clearable = <T extends z.ZodType>(schema: T) => schema.nullable().optional();

export const createEventInput = z
  .object({
    circleId: circleIdSchema,
    title: titleSchema,
    shareTitle: shareTitleSchema,
    category: categorySchema,
    startsAt: instantSchema.optional(),
    endsAt: instantSchema.optional(),
    locationLabel: locationLabelSchema.optional(),
    locationPrivate: locationPrivateSchema.optional(),
    practicalNote: practicalNoteSchema.optional(),
  })
  .superRefine((event, ctx) => {
    if (event.startsAt === undefined && event.endsAt === undefined) return;
    if (event.startsAt === undefined || event.endsAt === undefined) {
      ctx.addIssue({
        code: "custom",
        message: "An event with a time needs both a start and an end.",
        path: ["endsAt"],
      });
      return;
    }
    if (event.endsAt <= event.startsAt) {
      ctx.addIssue({
        code: "custom",
        message: "An event must end after it starts.",
        path: ["endsAt"],
      });
    }
  });

export const updateEventInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema,
  /**
   * The revision the caller was looking at. The boundary rejects a stale one rather
   * than overwriting somebody else's edit — and a MATERIAL change flips affected
   * commitments to "Please recheck" instead of silently re-consenting anyone
   * (invariant 4).
   */
  expectedRevision: revisionSchema,
  title: titleSchema.optional(),
  shareTitle: shareTitleSchema.optional(),
  category: categorySchema.optional(),
  startsAt: instantSchema.optional(),
  endsAt: instantSchema.optional(),
  locationLabel: clearable(locationLabelSchema),
  locationPrivate: clearable(locationPrivateSchema),
  practicalNote: clearable(practicalNoteSchema),
});

/** Draft -> scheduled (plan §10.2). Publishing changes no material field. */
export const publishEventInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema,
  expectedRevision: revisionSchema,
});

/** Scheduled -> completed (plan §10.2). */
export const completeEventInput = publishEventInput;

/**
 * Cancel an event.
 *
 * Cancellation is NOT deletion (invariant 6). The record and its history survive;
 * only the state changes, and erasure is a separate, controlled path (plan §10.12).
 *
 * NO `reason` FIELD. Settled by founder ruling on bead `hf-ft9`: V1 has none, for
 * plan fidelity — §28.6's `events` table defines no such field, and inventing one
 * here would have put free text into the only other place a cancel touches, the audit
 * row, where AGENTS.md forbids it outright.
 *
 * So this is an answer, not a gap. A parameter the backend accepts and then discards
 * would be worse than one it does not offer.
 */
export const cancelEventInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema,
  expectedRevision: revisionSchema,
});

/**
 * Hard-delete an event (EVT-013, plan §10.12).
 *
 * Legal ONLY for a draft with no meaningful history. Everything published is
 * cancelled instead — a cancelled hospital run is a fact the family may need to look
 * back on, and deleting it quietly rewrites what happened.
 */
export const deleteDraftEventInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema,
  expectedRevision: revisionSchema,
});

export const getEventInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema,
});

/**
 * Agenda listing (EVT-015). The range is optional but the query is always bounded to
 * one circle by index; an undated event has no place in a range and appears only in
 * the unbounded form.
 */
export const listEventsInput = z.object({
  circleId: circleIdSchema,
  fromInclusive: instantSchema.optional(),
  toExclusive: instantSchema.optional(),
});

export const listEventRevisionsInput = z.object({
  circleId: circleIdSchema,
  eventId: eventIdSchema,
});

export type CreateEventInput = z.input<typeof createEventInput>;
export type UpdateEventInput = z.input<typeof updateEventInput>;
export type PublishEventInput = z.input<typeof publishEventInput>;
export type CompleteEventInput = z.input<typeof completeEventInput>;
export type CancelEventInput = z.input<typeof cancelEventInput>;
export type DeleteDraftEventInput = z.input<typeof deleteDraftEventInput>;
export type GetEventInput = z.input<typeof getEventInput>;
export type ListEventsInput = z.input<typeof listEventsInput>;
export type ListEventRevisionsInput = z.input<typeof listEventRevisionsInput>;

/** The four stored event states (plan §28.3). Coverage is NOT one of them (EVT-018). */
export type EventStateView = "draft" | "scheduled" | "completed" | "cancelled";

/**
 * An event as the UI receives it.
 *
 * There is deliberately no `covered`, `status` or `coverage` field: an event is never
 * itself covered (EVT-018, plan §9.5) — its linked needs are, and they carry their
 * own `CoverageView`.
 */
export interface EventView {
  readonly eventId: string;
  readonly title: string;
  readonly shareTitle: string;
  readonly category: string;
  readonly startsAt?: number;
  readonly endsAt?: number;
  readonly locationLabel?: string;
  readonly locationPrivate?: string;
  readonly practicalNote?: string;
  readonly state: EventStateView;
  readonly revision: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly cancelledAt?: number;
}

/**
 * One entry of the event's material history (EVT-014, plan §28.6).
 *
 * Carries only the fields whose change can invalidate somebody's agreement, and
 * carries the location as a FINGERPRINT rather than the address — plan §28.6: "do not
 * duplicate private notes into revision history". It is enough to say "the location
 * changed"; the address itself stays in exactly one place.
 */
export interface EventRevisionView {
  readonly revision: number;
  readonly startsAt?: number;
  readonly endsAt?: number;
  readonly locationFingerprint?: string;
  readonly changedByPersonId: string;
  readonly changedAt: number;
}

export interface EventWritten {
  readonly eventId: string;
  readonly revision: number;
  /**
   * Whether this edit disturbed anybody's agreement.
   *
   * The UI needs it to know whether to say "saved" or "we've asked John and Sarah to
   * recheck" (plan §10.8: there is no hidden invalidation). It mirrors the kernel's
   * `ChangeClassification["outcome"]` exactly.
   */
  readonly outcome: "no_change" | "cosmetic" | "needs_editor_decision" | "material";
}

/** A deleted draft has no revision to report — the record is gone (EVT-013). */
export interface EventDeleted {
  readonly eventId: string;
  readonly deleted: true;
}

export type CreateEventResult = Result<EventWritten, EventRejection>;
export type UpdateEventResult = Result<EventWritten, EventRejection>;
export type PublishEventResult = Result<EventWritten, EventRejection>;
export type CompleteEventResult = Result<EventWritten, EventRejection>;
export type CancelEventResult = Result<EventWritten, EventRejection>;
export type DeleteDraftEventResult = Result<EventDeleted, EventRejection>;
export type GetEventResult = Result<EventView, EventRejection>;
export type ListEventRevisionsResult = Result<readonly EventRevisionView[], EventRejection>;
